import { useEffect, useState, useCallback } from 'react';
import Datatable from '@/components/common/Datatable';
import { Box, Divider, HStack, Icon, Switch, Text, useDisclosure } from '@chakra-ui/react';
import EyeIcon from '../../../assets/svg/eye.svg?react';
import { useParams } from 'react-router-dom';
import { Column } from 'react-table';
import {
  useDeleteReview,
  useGetReviewsByProduct,
  useHideReview,
  useUnHideReview,
} from '@/services/reviews';
import { StarIcon } from '@chakra-ui/icons';
import { IReview } from '@/services/reviews/types';
import { convertDateFormat } from '@/utils/dateConverter';
import CustomConfirmationModal from '@/components/common/ConfirmationModal';
import ConfirmDeleteIcon from '@/assets/svg/delete-bg.svg?react';
import useCustomToast from '@/components/common/Toast';
import useErrorHandler from '@/utils/errorHandler';
import { AxiosError } from 'axios';
import FormModal from '@/components/common/FormModal';
import ReportsByReview from './Reports';

const ReviewsByProduct = () => {
  const { reviewsId: id } = useParams<string>();
  const { isOpen, onClose: onCloseDelete } = useDisclosure();
  const {
    isOpen: isReportModalOpen,
    onOpen: onReportModalOpen,
    onClose: onReportModalClose,
  } = useDisclosure();

  const [selectedReview, setSelectedReview] = useState<IReview | null>(null);

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });

  // Default sort: newest first → use 'updated_at' (backend field)
  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([
    { id: 'updated_at', desc: true },
  ]);

  const { showToast } = useCustomToast();
  const { handleError } = useErrorHandler();

  // Convert sorting → ordering string for API
  const ordering =
    sorting.length > 0 ? sorting.map((s) => (s.desc ? `-${s.id}` : s.id)).join(',') : undefined;

  const {
    data: Products,
    isLoading,
    isError,
  } = useGetReviewsByProduct({
    id: id ?? '',
    page: pagination.page,
    ordering,
  });

  const { mutateAsync: deleteReview, isLoading: isDeletingReview } = useDeleteReview();
  const { mutateAsync: hideReviewAsync, isLoading: isHiding } = useHideReview();
  const { mutateAsync: unhideReviewAsync, isLoading: isUnhiding } = useUnHideReview();

  const isToggling = isHiding || isUnhiding;

  // Local optimistic UI for hide/unhide
  const [hiddenStates, setHiddenStates] = useState<Record<number, boolean>>({});
  const [togglingReviewId, setTogglingReviewId] = useState<number | null>(null);

  const handleDeleteConfirm = async () => {
    if (!selectedReview) return;

    try {
      await deleteReview(selectedReview.id.toString());
      setSelectedReview(null);
      onCloseDelete();
      showToast('success', 'Review deleted successfully');
    } catch (error: unknown) {
      if (error instanceof AxiosError) handleError(error);
    }
  };

  // Sync server `is_hidden` → local state
  useEffect(() => {
    if (Products?.results) {
      setHiddenStates((prev) => {
        const updated = { ...prev };
        Products.results.forEach((review) => {
          if (!(review.id in updated)) {
            updated[review.id] = review.is_hidden;
          }
        });
        return updated;
      });
    }
  }, [Products]);

  // Update pagination from API response
  useEffect(() => {
    if (Products) {
      setPagination((prev) => ({
        ...prev,
        pageSize: 10,
        totalCount: Products.count,
        totalPages: Products.page_count,
      }));
    }
  }, [Products]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  // Reset page when sorting changes
  const handleSortingChange = useCallback(
    (updater: React.SetStateAction<{ id: string; desc: boolean }[]>) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);
      setPagination((prev) => ({ ...prev, page: 1 }));
    },
    [sorting],
  );

  // Toggle hide/unhide
  const handleToggle = async (reviewId: string, targetChecked: boolean) => {
    const numericId = Number(reviewId);
    const prev = hiddenStates[numericId];
    if (targetChecked === prev) return;

    setHiddenStates((prev) => ({ ...prev, [numericId]: targetChecked }));
    setTogglingReviewId(numericId);

    try {
      if (targetChecked) {
        await hideReviewAsync({ id: reviewId });
      } else {
        await unhideReviewAsync({ id: reviewId });
      }
      showToast('success', 'Review visibility updated');
    } catch {
      setHiddenStates((prev) => ({ ...prev, [numericId]: !targetChecked }));
      showToast('error', 'Failed to update visibility');
    } finally {
      setTogglingReviewId(null);
    }
  };

  const columns: Column<IReview>[] = [
    {
      Header: 'Customer Name',
      // @ts-expect-error – disableSortBy is supported in react-table v7
      disableSortBy: true,
      Cell: ({ row }: { row: { original: IReview } }) => {
        const { user_first_name, user_last_name } = row.original;
        const name = [user_first_name, user_last_name].filter(Boolean).join(' ');
        return name ? (
          <Text minW="200px">{name}</Text>
        ) : (
          <Divider w="20px" borderColor="text.primary" />
        );
      },
    },
    {
      Header: 'Headline',
      // @ts-expect-error – disableSortBy is supported in react-table v7
      disableSortBy: true,
      Cell: ({ row }: { row: { original: IReview } }) => {
        const headline = row.original.headline;
        return headline ? (
          <Text minW="200px">{headline}</Text>
        ) : (
          <Divider w="20px" borderColor="text.primary" />
        );
      },
    },
    {
      Header: 'Review',
      // @ts-expect-error – disableSortBy is supported in react-table v7
      disableSortBy: true,
      Cell: ({ row }: { row: { original: IReview } }) => {
        const text = row.original.review_text;
        return text ? (
          <Text minW="200px">{text}</Text>
        ) : (
          <Divider w="20px" borderColor="text.primary" />
        );
      },
    },
    {
      Header: 'Rating',
      id: 'rating',
      accessor: 'rating' as keyof IReview,
      Cell: ({ row }: { row: { original: IReview } }) => {
        const rating = row.original.rating;
        return rating ? (
          <HStack gap={2}>
            <Icon as={StarIcon} color="brand.secondary.DEFAULT" />
            <Text>{rating}</Text>
          </HStack>
        ) : (
          <Divider w="20px" borderColor="text.primary" />
        );
      },
    },
    {
      Header: 'Reported Count',
      id: 'reported_count',
      accessor: 'reported_count' as keyof IReview,
      Cell: ({ row }: { row: { original: IReview } }) => (
        <Text>{row.original.reported_count ?? 0}</Text>
      ),
    },
    {
      Header: 'Reviewed On',
      id: 'updated_at',
      accessor: 'created_at' as keyof IReview,
      Cell: ({ row }: { row: { original: IReview } }) => {
        const date = row.original.created_at;
        return date ? (
          <Text>{convertDateFormat(date)}</Text>
        ) : (
          <Divider w="20px" borderColor="text.primary" />
        );
      },
    },
    {
      Header: 'Hide/unHide',
      id: 'is_hidden',
      accessor: 'is_hidden' as keyof IReview,
      Cell: ({ row }: { row: { original: IReview } }) => {
        const { id, is_hidden } = row.original;
        return (
          <Switch
            colorScheme="brand.primary"
            isChecked={hiddenStates[id] ?? is_hidden}
            onChange={(e) => handleToggle(id.toString(), e.target.checked)}
            isDisabled={togglingReviewId === id || isToggling}
          />
        );
      },
    },
    {
      Header: 'Actions',
      id: 'actions',
      // @ts-expect-error – disableSortBy is supported in react-table v7
      disableSortBy: true,
      Cell: ({ row }: { row: { original: IReview } }) => (
        <HStack gap={2}>
          <Icon
            as={EyeIcon}
            boxSize={5}
            cursor="pointer"
            onClick={() => {
              setSelectedReview(row.original);
              onReportModalOpen();
            }}
          />
        </HStack>
      ),
    },
  ];

  return (
    <Box p={6} borderRadius="xl">
      <Datatable
        data={Products?.results ?? []}
        isLoading={isLoading}
        isError={isError}
        columns={columns}
        pagination={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalCount: pagination.totalCount,
          totalPages: pagination.totalPages,
        }}
        onPageChange={handlePageChange}
        sticky={true}
        hideFilters={true}
        headerText={`Reviews: ${Products?.results?.[0]?.product_name ?? ''}`}
        // Server-side sorting
        sorting={sorting}
        onSortingChange={handleSortingChange}
        manualSorting={true}
      />

      <CustomConfirmationModal
        isLoading={isDeletingReview}
        isOpen={isOpen}
        onClose={onCloseDelete}
        onConfirm={handleDeleteConfirm}
        icon={ConfirmDeleteIcon}
        title="Delete Review?"
        subtitle="Are you sure you want to delete this review?"
        description="This action cannot be undone, and you won't be able to track this review anywhere. This will remove the review permanently."
        buttonText="Confirm"
      />

      <FormModal
        title="Review Reports"
        isOpen={isReportModalOpen}
        onClose={onReportModalClose}
        size="3xl"
        children={<ReportsByReview review={selectedReview} />}
        saveText="OK"
        onConfirm={onReportModalClose}
      />
    </Box>
  );
};

export default ReviewsByProduct;
